import { useEffect, useMemo, useRef, useState } from 'react';
import type { Application } from '@interlucent/organization-protocol';
import { useStore } from '../../store';
import { send } from '../../ws';
import { useFocusTrap } from '../../utils/focusTrap';
import { correlationId } from '@interlucent/ids';
import { waitForAck } from '../../utils/waitForAck';
import { logger } from '../../log';
import styles from '../CreateProjectDialog.module.css';

const log = logger('move-app');

interface MoveApplicationDialogProps {
  application: Application;
  onClose: () => void;
  onError?: (message: string) => void;
}

export function MoveApplicationDialog({
  application,
  onClose,
  onError,
}: MoveApplicationDialogProps) {
  const modalRef = useRef<HTMLDivElement>(null);
  const projects = useStore((s) => s.projects);
  const [targetProjectId, setTargetProjectId] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useFocusTrap(modalRef, true);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  const targetOptions = useMemo(() => {
    return Object.values(projects)
      .filter((p) => p.id !== application.projectId)
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [projects, application.projectId]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!targetProjectId) return;
    setSubmitting(true);
    const corr = correlationId();
    log.info('sending application.move', { appId: application.id, targetProjectId, corr });
    send(
      'application.move',
      {
        appId: application.id,
        targetProjectId,
        rowVersion: application.rowVersion,
      },
      corr,
    );
    try {
      await waitForAck(corr);
      onClose();
    } catch (err: any) {
      log.error('move failed', err);
      onError?.(err?.message || 'Failed to move application');
      setSubmitting(false);
    }
  };

  return (
    <div
      className={styles.modalOverlay}
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby="move-app-title"
    >
      <div ref={modalRef} className={styles.modal} onClick={(e) => e.stopPropagation()}>
        <h2 id="move-app-title" className={styles.modalTitle}>
          Move "{application.name}"
        </h2>
        <form onSubmit={handleSubmit}>
          <div className={styles.formGroup}>
            <label htmlFor="move-target-project" className={styles.label}>
              Destination Project *
            </label>
            {targetOptions.length === 0 ? (
              <p>No other projects available. Create another project first.</p>
            ) : (
              <select
                id="move-target-project"
                value={targetProjectId}
                onChange={(e) => setTargetProjectId(e.target.value)}
                className={styles.input}
                required
              >
                <option value="" disabled>
                  Select a project…
                </option>
                {targetOptions.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
              </select>
            )}
          </div>
          <p style={{ fontSize: '0.85em', opacity: 0.8, marginTop: 0 }}>
            Existing deployment-profile mappings for this application will be removed.
            The application's URL slug may change in the new project.
          </p>
          <div className={styles.buttonGroup}>
            <button
              type="button"
              onClick={onClose}
              className={`${styles.button} ${styles.buttonSecondary}`}
              disabled={submitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              className={styles.button}
              disabled={!targetProjectId || submitting || targetOptions.length === 0}
            >
              {submitting ? 'Moving…' : 'Move'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
